import { Link } from 'react-router-dom';
import { formatBrl } from '../utils/currency.js';

/** @typedef {'d7'|'d3'|'d1'|'d0'|'overdue'} BillingReminderTier */

/**
 * @typedef {Object} BillingReminderItem
 * @property {number} tenant_id
 * @property {string} tenant_nome
 * @property {string} due_date
 * @property {number} days_until_due
 * @property {BillingReminderTier} tier
 * @property {string} amount_brl
 * @property {string} pay_url
 * @property {boolean} [blocked]
 */

/** @param {BillingReminderTier} tier */
function tierStyles(tier) {
  switch (tier) {
    case 'overdue':
      return {
        shell: 'border-rose-500/45 bg-gradient-to-r from-rose-500/15 to-red-500/10 dark:from-rose-950/40 dark:to-red-950/30',
        badge: 'bg-rose-600/15 text-rose-900 dark:text-rose-100',
      };
    case 'd0':
      return {
        shell: 'border-orange-500/40 bg-gradient-to-r from-orange-500/15 to-amber-500/10 dark:from-orange-950/35 dark:to-amber-950/25',
        badge: 'bg-orange-500/15 text-orange-950 dark:text-orange-100',
      };
    case 'd1':
      return {
        shell: 'border-amber-500/40 bg-gradient-to-r from-amber-500/15 to-yellow-500/10 dark:from-amber-950/30 dark:to-yellow-950/20',
        badge: 'bg-amber-500/15 text-amber-950 dark:text-amber-100',
      };
    case 'd3':
      return {
        shell: 'border-sky-500/35 bg-gradient-to-r from-sky-500/12 to-cyan-500/8 dark:from-sky-950/30 dark:to-cyan-950/20',
        badge: 'bg-sky-500/15 text-sky-950 dark:text-sky-100',
      };
    case 'd7':
    default:
      return {
        shell: 'border-indigo-500/30 bg-gradient-to-r from-indigo-500/10 to-violet-500/8 dark:from-indigo-950/25 dark:to-violet-950/20',
        badge: 'bg-indigo-500/15 text-indigo-950 dark:text-indigo-100',
      };
  }
}

/** @param {BillingReminderItem} item */
function formatDueDateBr(item) {
  const raw = String(item.due_date || '').slice(0, 10);
  const parts = raw.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return raw;
}

/** @param {BillingReminderItem} item */
function reminderCopy(item) {
  const loja = String(item.tenant_nome || 'sua loja').trim() || 'sua loja';
  const data = formatDueDateBr(item);
  const valor = formatBrl(Number(item.amount_brl || 0));

  switch (item.tier) {
    case 'overdue':
      return {
        title: 'Loja temporariamente pausada',
        body: `A mensalidade de ${loja} está em atraso (${valor}). Regularize quando puder para voltar a vender com tranquilidade.`,
        badge: 'Atenção',
      };
    case 'd0':
      return {
        title: 'Vencimento hoje',
        body: `Hoje (${data}) vence a mensalidade de ${loja} (${valor}). É só um lembrete amigável — pague com calma para manter tudo online.`,
        badge: 'Hoje',
      };
    case 'd1':
      return {
        title: 'Vence amanhã',
        body: `A mensalidade de ${loja} vence amanhã (${data}, ${valor}). Organize o pagamento com antecedência e evite surpresas.`,
        badge: 'Amanhã',
      };
    case 'd3':
      return {
        title: 'Vence em 3 dias',
        body: `Só passando para avisar: em 3 dias (${data}) vence a mensalidade de ${loja} (${valor}). Nada urgente — é um alerta para você se programar.`,
        badge: '3 dias',
      };
    case 'd7':
    default:
      return {
        title: 'Lembrete de vencimento',
        body: `Daqui a 7 dias (${data}) vence a mensalidade de ${loja} (${valor}). Lembrete amigável para manter sua loja sempre disponível.`,
        badge: '7 dias',
      };
  }
}

/**
 * @param {{ primary?: BillingReminderItem | null, extraCount?: number, adminPreview?: boolean }} props
 */
export function BillingReminderBanner({ primary, extraCount = 0, adminPreview = false }) {
  if (!primary || typeof primary !== 'object') {
    return null;
  }

  const styles = tierStyles(primary.tier);
  const copy = reminderCopy(primary);
  const payHref = primary.pay_url || `/app/mensalidade?billing=pay&tenant=${primary.tenant_id}`;

  return (
    <div
      className={`-mt-2 mb-6 rounded-2xl border px-4 py-3 text-sm backdrop-blur ${styles.shell}`}
      role="status"
      aria-live="polite"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            {adminPreview ? (
              <span className="rounded-full bg-violet-600/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-950 dark:text-violet-100">
                Visualização admin
              </span>
            ) : null}
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${styles.badge}`}>
              {copy.badge}
            </span>
            <strong className="text-ink dark:text-mist">{copy.title}</strong>
          </div>
          <p className="leading-relaxed text-ink/85 dark:text-mist/90">{copy.body}</p>
          {extraCount > 0 ? (
            <p className="text-xs text-ink/60 dark:text-frost-muted">
              Há mais {extraCount} lembrete{extraCount === 1 ? '' : 's'} em outras empresas — veja em{' '}
              <Link to="/app/mensalidade" className="font-semibold underline underline-offset-2">
                Mensalidade
              </Link>
              .
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          {payHref.startsWith('http') ? (
            <a
              href={payHref}
              className="inline-flex items-center justify-center rounded-xl bg-ink px-4 py-2 text-xs font-bold uppercase tracking-wide text-mist shadow-sm transition hover:opacity-90 dark:bg-mist dark:text-ink"
            >
              Abrir pagamento
            </a>
          ) : (
            <Link
              to={payHref}
              className="inline-flex items-center justify-center rounded-xl bg-ink px-4 py-2 text-xs font-bold uppercase tracking-wide text-mist shadow-sm transition hover:opacity-90 dark:bg-mist dark:text-ink"
            >
              Abrir pagamento
            </Link>
          )}
          <Link
            to="/app/mensalidade"
            className="inline-flex items-center justify-center rounded-xl border border-ink/15 bg-white/70 px-4 py-2 text-xs font-semibold text-ink dark:border-white/15 dark:bg-white/10 dark:text-mist"
          >
            Ver mensalidades
          </Link>
        </div>
      </div>
    </div>
  );
}
